import { useAuthNavigation, useSession } from '@/lib/auth/useSession'

import { ROLES } from '../laws'

/**
 * The masthead: brand, global search, the display controls and the actor.
 *
 * Verbatim from `docs/design/reference/template.html` lines 42–66.
 *
 * The search field covers every identifier an operator might be handed —
 * customer, IBAN, provider reference, posting id, decision_ref, idempotency
 * key — because the alternative is knowing which screen owns which identifier
 * before you can look anything up. It opens the palette rather than searching
 * in place, so one keystroke is enough from anywhere.
 *
 * The role selector is PRESENTATION ONLY. It changes what this console shows;
 * go-wallet decides what a session may actually do, and an Ory session proves
 * who, never may-they (CLAUDE.md Part I §6).
 */

interface Props {
  readonly role: string
  readonly onRole: (role: string) => void
  readonly density: 34 | 44
  readonly onToggleDensity: () => void
  readonly theme: 'light' | 'dark'
  readonly onToggleTheme: () => void
  readonly onOpenPalette: () => void
  readonly onToggleHelp: () => void
  readonly onHome: () => void

  /**
   * Whether the screen on show was answered by the BACKEND rather than drawn
   * from the local corpus.
   *
   * The second half of #200. On a live screen the local role is deliberately
   * not consulted — a screen the server answered is not hidden by a guess — so
   * a preview changes nothing there either, for an operator who DOES hold
   * roles. Same silence, different cause, and it deserves the same sentence.
   *
   * Optional and defaulting to false: this is presentation, and a caller that
   * has not got the answer yet should not be forced to lie about it.
   */
  readonly screenIsServerAnswered?: boolean
}

export function MastHead({
  role,
  onRole,
  density,
  onToggleDensity,
  theme,
  onToggleTheme,
  onOpenPalette,
  onToggleHelp,
  onHome,
  screenIsServerAnswered = false,
}: Props) {
  const session = useSession()
  // Null while we do not know — which is not the same as "holds no roles".
  // See the Actor component below; a 404 from an endpoint that does not exist
  // yet must not be rendered as a permission answer.
  const heldRoles = session.status === 'signed-in' ? session.session.roles : null

  // THREE STATES, AND THE SWITCHER USED TO COLLAPSE THEM INTO TWO (#200).
  //
  // `heldRoles === null` meant "we do not know", and a session with NO STAFF
  // RECORD landed there too — so the control stayed fully live, offered nine
  // options, updated its own value, and could not affect a single pixel. That
  // is what milen reported: "selecting the admin role does nothing."
  //
  // It was doing exactly what it is written to do. Every live screen is refused
  // for this session, so there is no data for a preview to narrow, and the
  // tooltip it showed — "Preview a role. The backend enforces the real one." —
  // is true and useless, because what the backend is enforcing is "you are not
  // staff".
  //
  // So the two are separated. UNKNOWN still offers the full list, because the
  // switcher is a preview of what each role SEES and is useful before anyone is
  // signed in. NO STAFF RECORD is a known answer and the control says so.
  const rolesUnknowable =
    session.status === 'not-staff' || session.status === 'disabled' || session.status === 'signed-out'
  const previewIsInert = rolesUnknowable || (heldRoles !== null && heldRoles.length === 0)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '6px 14px',
        flex: '0 0 auto',
        minHeight: '46px',
        padding: '4px 12px',
        borderBottom: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
      }}
    >
      <button
        type="button"
        onClick={onHome}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: 0,
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          color: 'var(--k-ink)',
        }}
      >
        <span style={{ width: '12px', height: '12px', background: 'var(--k-accent)' }} />
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            fontWeight: 600,
            fontSize: '13px',
            letterSpacing: '.16em',
          }}
        >
          KOVA OPS
        </span>
      </button>

      <button
        type="button"
        className="k-hov-edge"
        onClick={onOpenPalette}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: '1 1 180px',
          minWidth: 0,
          maxWidth: '620px',
          height: '28px',
          padding: '0 10px',
          border: '1px solid var(--k-line)',
          background: 'var(--k-bg)',
          color: 'var(--k-mut)',
          font: 'inherit',
          cursor: 'text',
          textAlign: 'left',
        }}
      >
        <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11px' }}>/</span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          Search customer, IBAN, provider ref, posting id, decision_ref, idempotency key…
        </span>
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            fontSize: '10.5px',
            border: '1px solid var(--k-line)',
            padding: '0 4px',
          }}
        >
          ⌘K
        </span>
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1px',
          marginLeft: 'auto',
          background: 'var(--k-line)',
        }}
      >
        <button type="button" className="k-hov" onClick={onToggleDensity} title="Density" style={CTL}>
          Density · {density === 34 ? 'compact' : 'comfortable'}
        </button>
        <button type="button" className="k-hov" onClick={onToggleTheme} title="Theme" style={CTL}>
          Theme · {theme}
        </button>
        <button
          type="button"
          className="k-hov"
          onClick={onToggleHelp}
          title="Shortcuts"
          style={{ ...CTL, fontFamily: 'var(--k-mono)', fontSize: '11px', textTransform: 'none' }}
        >
          ?
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          paddingLeft: '12px',
          borderLeft: '1px solid var(--k-line)',
          height: '26px',
        }}
      >
        {/* The roles the session actually holds, when we know them. Until then
            the full list, because the switcher is a preview of what each role
            SEES and is useful before anyone is signed in.

            Either way this is presentation. go-wallet decides what may happen,
            on every request, whether or not the console drew the button — so a
            switcher that offered a role you do not hold would mislead you, not
            escalate you. */}
        <select
          value={role}
          onChange={(e) => onRole(e.target.value)}
          disabled={previewIsInert}
          title={
            previewIsInert
              ? 'No staff record on this session, so there is nothing to preview — every screen is refused for the same reason whichever role is picked. This does not restrict you; the backend already has.'
              : heldRoles === null
                ? 'Preview a role. The backend enforces the real one.'
                : 'Preview what a role sees. A preview can only ever show LESS than your session holds — it cannot widen access, and the backend enforces the real answer either way.'
          }
          aria-label="Acting role"
          style={{
            opacity: previewIsInert ? 0.55 : 1,
            cursor: previewIsInert ? 'not-allowed' : 'pointer',
            height: '26px',
            border: '1px solid var(--k-line)',
            background: 'var(--k-bg)',
            color: 'var(--k-ink)',
            font: 'inherit',
            fontSize: '11.5px',
            padding: '0 6px',
            borderRadius: 0,
          }}
        >
          {/* Every role, always, with the ones this session holds marked.
              It used to list only the held roles, which made the switcher
              useless for its designed purpose — looking at what a colleague in
              another role sees — and left the selected value outside its own
              options whenever the session held anything but Ops.

              Offering all seven is safe because a preview NARROWS: the console
              intersects it with what the session actually holds, so picking
              Admin as a support agent shows a support agent's console. And
              nothing here is enforcement in any case. */}
          {ROLES.map((r) => {
            const held = heldRoles?.some((h) => h.toLowerCase() === r.toLowerCase()) ?? false
            return (
              <option key={r} value={r}>
                {heldRoles === null || !held ? r : `${r} · yours`}
              </option>
            )
          })}
        </select>

        {/* THE REASON IS ON THE SCREEN, NOT ONLY IN THE TOOLTIP (#200).
            A disabled control whose explanation lives in a `title` is the same
            defect one level down: it is discoverable by hovering, and the
            person who filed this did not hover — they picked Admin and watched
            nothing happen. */}
        {previewIsInert ? (
          <span
            data-testid="role-preview-note"
            style={{ fontSize: '11px', color: 'var(--k-dim)', whiteSpace: 'nowrap' }}
          >
            no staff record &mdash; nothing to preview
          </span>
        ) : screenIsServerAnswered ? (
          /* The other silence, and it happens to an operator who DOES hold
             roles. On a live screen the local role is not consulted at all —
             a screen the backend answers is not hidden by a guess. So the
             preview is real and simply does not reach this screen. Saying so
             is the difference between a control that looks broken and one
             that is scoped.

             READ FROM rather than ANSWERED BY, because this covers every state
             a wired screen can be in — in flight, failed, empty, populated —
             and the local rule is skipped in all four. "the server's answer"
             was a claim that is false while a read is still running (#221). */
          <span
            data-testid="role-preview-note"
            style={{ fontSize: '11px', color: 'var(--k-dim)', whiteSpace: 'nowrap' }}
          >
            preview does not apply &mdash; this screen is read from the server
          </span>
        ) : null}

        <Actor />
      </div>
    </div>
  )
}

/**
 * Who is acting, in the guide's two-line slot.
 *
 * Every other figure on screen is design-corpus data and badged as such, but
 * WHO IS ACTING is an audit-adjacent claim. So this slot never guesses: it says
 * signed in, signed out, or that it does not know — and "does not know" is a
 * real state, because `/v1/admin/me` does not exist yet (go-wallet#284) and a
 * 404 is not a sign-out.
 */
function Actor() {
  const state = useSession()
  const { signIn, signOut } = useAuthNavigation()

  const line2 = (text: string) => (
    <div style={{ color: 'var(--k-mut2)', fontSize: '10px', fontFamily: 'var(--k-mono)' }}>
      {text}
    </div>
  )

  if (state.status === 'signed-in') {
    const { displayName, subject, staffId } = state.session
    // An empty label is a real directory state, and the backend leaves it empty
    // on purpose rather than substituting the subject. So the console says the
    // name is unset instead of printing a uuid where a name belongs: a uuid is
    // not a name, it is not something an operator can act on, and it reads as a
    // fault rather than as a row somebody has not filled in. The subject is
    // still on the line below, where it belongs and where it is labelled.
    const named = displayName !== ''
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '11.5px',
              ...(named ? {} : { color: 'var(--k-mut2)', fontStyle: 'italic' }),
            }}
          >
            {named ? displayName : 'No name in the staff directory'}
          </div>
          {/* The subject is the audit log's actor, and `staff_id` is the
              reference an operator quotes when they raise a ticket about their
              own access. Both are references rather than identities, so both
              are mono and both are on the second line. */}
          {line2(staffId === null ? subject : `${subject} · staff ${String(staffId)}`)}
        </div>
        <button type="button" className="k-hov" onClick={() => { void signOut() }} style={AUTH_BUTTON}>
          Sign out
        </button>
      </div>
    )
  }

  if (
    state.status === 'signed-out' ||
    state.status === 'not-staff' ||
    state.status === 'disabled'
  ) {
    const notStaff = state.status === 'not-staff'
    const disabled = state.status === 'disabled'
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
          <div style={{ fontWeight: 600, fontSize: '11.5px' }}>
            {disabled ? 'Access revoked' : notStaff ? 'Not a staff account' : 'Not signed in'}
          </div>
          {/* A valid Ory session that is not in the staff directory is a
              different problem from having no session, and telling someone to
              "sign in" when they already did is how a support ticket starts. */}
          {/* Three different problems and three different next actions: log
              in, ask for an account, or ask why access was taken away. A
              revoked account offers no SIGN IN button — signing in again is
              what someone tries first and it will not help. */}
          {line2(
            disabled
              ? 'staff account disabled'
              : notStaff
                ? 'signed in, no staff record'
                : 'no staff session',
          )}
        </div>
        {!notStaff && !disabled && (
          <button type="button" className="k-hov" onClick={() => { void signIn() }} style={AUTH_BUTTON}>
            Sign in
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
      <div style={{ fontWeight: 600, fontSize: '11.5px' }}>
        {state.status === 'loading' ? 'Checking session…' : 'Session unknown'}
      </div>
      {line2(state.status === 'loading' ? 'asking the backend' : state.reason)}
    </div>
  )
}

const AUTH_BUTTON = {
  height: '22px',
  padding: '0 8px',
  border: '1px solid var(--k-line)',
  background: 'transparent',
  color: 'var(--k-ink)',
  font: 'inherit',
  fontSize: '10.5px',
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
} as const

const CTL = {
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  height: '26px',
  padding: '0 9px',
  border: 0,
  background: 'var(--k-panel)',
  color: 'var(--k-ink)',
  font: 'inherit',
  fontSize: '10.5px',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
} as const

/**
 * The standby banner. Law 18: on a standby cell every mutating control is
 * disabled under a visually distinct banner — not hidden, so the operator can
 * see what they would be able to do on the primary.
 *
 * Verbatim from template lines 68–74.
 */
export function StandbyBanner({ cellId }: { cellId: string }) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '7px 12px',
        background: 'var(--k-a-bg)',
        color: 'var(--k-a-fg)',
        borderBottom: '2px solid var(--k-a-fg)',
      }}
      role="status"
    >
      <span
        style={{
          fontWeight: 800,
          fontSize: '10.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
        }}
      >
        Standby cell — read only
      </span>
      <span style={{ fontSize: '12px' }}>
        This is the standby cell. Writes happen on the primary.
      </span>
      <span style={{ marginLeft: 'auto', fontFamily: 'var(--k-mono)', fontSize: '11px' }}>
        WALLET_CELL_ID={cellId} · WALLET_CELL_ROLE=standby · workers: none
      </span>
    </div>
  )
}

/**
 * Break-glass. An elevated session is loud for its whole duration, carries the
 * reason it was opened, and expires — the opposite of a permission quietly
 * granted and forgotten.
 *
 * Verbatim from template lines 75–81.
 */
export function BreakGlassBanner({ onEnd }: { onEnd: () => void }) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '7px 12px',
        background: 'var(--k-accent)',
        color: 'var(--k-accentink)',
        borderBottom: '2px solid var(--k-rule)',
      }}
      role="status"
    >
      <span
        style={{
          fontWeight: 800,
          fontSize: '10.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
        }}
      >
        Break-glass session active
      </span>
      <span style={{ fontSize: '12px' }}>
        Reason: D2 case 4118 requires ledger inspection beyond Ops scope · expires in 21 min
      </span>
      <button
        type="button"
        onClick={onEnd}
        style={{
          marginLeft: 'auto',
          height: '22px',
          padding: '0 8px',
          border: '1px solid currentColor',
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          fontSize: '11px',
          cursor: 'pointer',
        }}
      >
        End session
      </button>
    </div>
  )
}
